import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MOVES } from "../data/moves";
import { useSettings } from "../context/SettingsContext";
import CoachPlayers from "../components/CoachPlayers";
import BusinessPulseBar from "../components/BusinessPulseBar";
import FreeDrillsManager from "../components/FreeDrillsManager";
import ClubManager from "../components/ClubManager";
import AttendanceGrid from "../components/AttendanceGrid";
import TrialPassManager from "../components/TrialPassManager";
import SchoolManager from "../components/SchoolManager";
import ReadinessAnalytics from "../components/ReadinessAnalytics";
import TrainingPlans from "../components/TrainingPlans";
import CoachPotwHistory from "../components/CoachPotwHistory";
import LockerRoomManager from "../components/LockerRoomManager";
import MarketingKit from "../components/MarketingKit";
import BackToTop from "../components/BackToTop";
import OnField from "../components/coach/OnField";
import SessionHighlights from "../components/coach/SessionHighlights";
import CoachRecaps from "../components/coach/CoachRecaps";
import { ACADEMY_LOGO, ACADEMY_LOGO_H } from "../data/assets";
import ScrollFade from "../components/ScrollFade";
import { Lock, Save, LogOut, ArrowLeft, Video, CalendarDays, CalendarRange, Star, BarChart3, Eye, CheckCircle2, Download, Trophy, Link2, Copy, Mail, GraduationCap, Activity, Dumbbell, LifeBuoy, Plus, Trash2, ChevronDown, Radio , Clapperboard, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Coach tool: generate/copy a player's account setup link by email (for manual sending).
const ClaimLinkTool = ({ token }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (resend = false) => {
    const e = email.trim();
    if (!e) { toast.error("Enter the player's email."); return; }
    setLoading(true); if (!resend) setResult(null);
    try {
      const { data } = await axios.post(`${API}/coach/claim-link`, { email: e, resend },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (data.status === "ok" && resend) toast.success("Welcome email re-sent to the player.");
      else if (data.status === "already_active") toast.info("This player is already set up.");
    } catch (err) {
      setResult(null);
      toast.error(err?.response?.data?.detail || "Could not generate the link.");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.claim_url); toast.success("Claim link copied!"); }
    catch { toast.error("Copy failed — select and copy the link manually."); }
  };

  return (
    <div data-testid="claim-link-tool" className="bg-[#141414] border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1"><Link2 size={18} className="text-[#C41E24]" /><h3 className="font-headings text-xl uppercase tracking-wide">Account Setup Link</h3></div>
      <p className="text-sm text-white/50 mb-4">Generate a player's claim link to send manually (e.g. if the welcome email didn't arrive).</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid="claim-link-email"
          type="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          onKeyDown={(ev) => ev.key === "Enter" && run(false)}
          placeholder="player@email.com"
          className="flex-1 bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
        />
        <button data-testid="claim-link-get-btn" onClick={() => run(false)} disabled={loading}
          className="bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-wider px-5 py-3 rounded-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          <Link2 size={15} /> {loading ? "Working…" : "Get Link"}
        </button>
      </div>

      {result?.status === "ok" && (
        <div data-testid="claim-link-result" className="mt-4 rounded-lg border border-[#C41E24]/30 bg-[#0d0d0d] p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">Claim link for {result.email}</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="claim-link-url" readOnly value={result.claim_url}
              onFocus={(ev) => ev.target.select()}
              className="flex-1 bg-[#111] border border-white/10 rounded-md p-3 text-[#C41E24] text-xs font-mono focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#C41E24]" />
            <div className="flex gap-2">
              <button data-testid="claim-link-copy-btn" onClick={copy}
                className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-4 py-3 rounded-md transition-colors flex items-center gap-1.5"><Copy size={14} /> Copy</button>
              <button data-testid="claim-link-resend-btn" onClick={() => run(true)} disabled={loading}
                className="bg-white/5 border border-white/15 hover:border-white/40 text-white/80 text-xs font-bold uppercase tracking-wider px-4 py-3 rounded-md transition-colors flex items-center gap-1.5 disabled:opacity-60"><Mail size={14} /> Resend Email</button>
            </div>
          </div>
        </div>
      )}
      {result?.status === "already_active" && (
        <div data-testid="claim-link-active" className="mt-4 rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-sm text-white/80 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-green-400 shrink-0" /> {result.message}
        </div>
      )}
    </div>
  );
};
const TIER_TITLES = { touch: "Foundational Touches", move: "Signature Moves", combo: "Combination / Flow Moves" };
const MOVE_NAME = Object.fromEntries(MOVES.map((m) => [m.id, m.name]));

export default function Coach() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { bookingLink, videoOverrides, chapterVideos, featured, weekly, helpVideos, refresh } = useSettings();
  const [token, setToken] = useState(localStorage.getItem("coachToken") || "");
  const [passcode, setPasscode] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [chapterVids, setChapterVids] = useState({});
  const [showChapterVids, setShowChapterVids] = useState(false);
  const [showMoveVids, setShowMoveVids] = useState(false);
  const [booking, setBooking] = useState("");
  const [featVideo, setFeatVideo] = useState("");
  const [featTitle, setFeatTitle] = useState("");
  const [featNote, setFeatNote] = useState("");
  const [weeklyDrills, setWeeklyDrills] = useState(["", "", ""]);
  const [weeklyTipVideo, setWeeklyTipVideo] = useState("");
  const [weeklyTipTitle, setWeeklyTipTitle] = useState("");
  const [helpVids, setHelpVids] = useState([]);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("content");
  const [openPlayerId, setOpenPlayerId] = useState("");
  const [returnTo, setReturnTo] = useState("");
  const [playersFilter, setPlayersFilter] = useState("all");
  const [playersSort, setPlayersSort] = useState("");
  const [trialsFilter, setTrialsFilter] = useState("all");
  const [pulseKey, setPulseKey] = useState(0);

  // Jump from a Business Pulse metric straight to where the work lives.
  const pulseJump = (target, opts = {}) => {
    setTab(target);
    if (target === "players") {
      if (opts.filter) setPlayersFilter(opts.filter);
      if (opts.sort) setPlayersSort(opts.sort);
    }
    if (target === "trials") setTrialsFilter(opts.filter || "all");
    setPulseKey((k) => k + 1);
    if (opts.scroll) {
      setTimeout(() => document.querySelector(`[data-testid="${opts.scroll}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Deep-link from the Club View dashboard: /coach?tab=players&player=<id>&from=<returnUrl>
  useEffect(() => {
    const t = searchParams.get("tab");
    const pid = searchParams.get("player");
    const from = searchParams.get("from");
    if (t) setTab(t);
    if (pid) setOpenPlayerId(pid);
    if (from) setReturnTo(from);
    if (t || pid || from) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab"); next.delete("player"); next.delete("from");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { setOverrides(videoOverrides || {}); }, [videoOverrides]);
  useEffect(() => { setChapterVids(chapterVideos || {}); }, [chapterVideos]);
  useEffect(() => { setBooking(bookingLink || ""); }, [bookingLink]);
  useEffect(() => {
    setFeatVideo(featured?.video || "");
    setFeatTitle(featured?.title || "");
    setFeatNote(featured?.note || "");
  }, [featured]);
  useEffect(() => {
    const d = weekly?.drills || [];
    setWeeklyDrills([d[0] || "", d[1] || "", d[2] || ""]);
    setWeeklyTipVideo(weekly?.tipVideo || "");
    setWeeklyTipTitle(weekly?.tipTitle || "");
  }, [weekly]);
  useEffect(() => { setHelpVids(helpVideos || []); }, [helpVideos]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/analytics/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setStats(data))
      .catch((err) => { if (err?.response?.status === 401 || err?.response?.status === 403) { localStorage.removeItem("coachToken"); setToken(""); } });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/coach/login`, { passcode });
      localStorage.setItem("coachToken", data.access_token);
      setToken(data.access_token);
      toast.success("Welcome, Coach.");
    } catch (err) {
      toast.error("Invalid passcode. Try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const [sendingPotw, setSendingPotw] = useState(false);
  const sendPotw = async () => {
    setSendingPotw(true);
    try {
      const { data } = await axios.post(`${API}/coach/send-potw`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.sent) toast.success(`Player of the Week emailed: ${data.player.name} (${data.player.streak}-day streak) 🏆`);
      else toast.info("No player has an active streak yet — nothing to send this week.");
    } catch (err) {
      toast.error("Could not send the shout-out. Try again.");
    } finally {
      setSendingPotw(false);
    }
  };

  const logout = () => { localStorage.removeItem("coachToken"); setToken(""); setPasscode(""); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/coach/settings`,
        { booking_link: booking, video_overrides: overrides, chapter_videos: chapterVids, featured_video: featVideo, featured_title: featTitle, featured_note: featNote, weekly_drills: weeklyDrills.filter(Boolean), weekly_tip_video: weeklyTipVideo, weekly_tip_title: weeklyTipTitle, help_videos: helpVids.filter((v) => v.url && v.url.trim()) },
        { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast.success("Saved! Players now see your updates.");
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!token || window.innerWidth >= 640) return;
    document.querySelector(`[data-testid="coach-tab-${tab}"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [tab, token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center px-6 grain">
        <div className="absolute inset-0 red-glow pointer-events-none" />
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-sm bg-[#141414] border border-white/10 rounded-xl p-8"
        >
          <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-40 h-auto object-contain mb-5" />
          <div className="flex items-center gap-2 mb-1"><Lock size={16} className="text-[#C41E24]" /><span className="text-[10px] font-bold uppercase tracking-widest text-[#C41E24]">Coach Mode</span></div>
          <h1 className="font-headings text-3xl uppercase tracking-wide mb-6">Coach Access</h1>
          <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-2 block">Passcode</label>
          <input
            data-testid="coach-passcode-input"
            type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)} autoFocus
            className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white mb-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
            placeholder="Enter coach passcode"
          />
          <button data-testid="coach-login-btn" type="submit" disabled={loggingIn}
            className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors disabled:opacity-60">
            {loggingIn ? "Checking…" : "Enter Coach Mode"}
          </button>
          <button type="button" onClick={() => navigate("/")} className="w-full mt-3 text-xs font-bold uppercase tracking-wider text-white/40 hover:text-white flex items-center justify-center gap-2">
            <ArrowLeft size={14} /> Back to the Academy home
          </button>
        </motion.form>
      </div>
    );
  }

  const grouped = { touch: [], move: [], combo: [] };
  MOVES.forEach((m) => grouped[m.tier].push(m));

  return (
    <div className="min-h-screen bg-[#111111] text-white">
      <header className="sticky top-0 z-30 bg-[#111111]/85 backdrop-blur-xl border-b border-white/10 px-4 sm:px-6 pb-4 header-safe flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-9 sm:h-12 w-auto object-contain shrink-0" />
          <div className="min-w-0 hidden sm:block">
            <div className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold whitespace-nowrap">Coach Mode</div>
            <div className="font-headings text-base uppercase tracking-wide truncate">Content Manager</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="coach-send-potw-btn" onClick={sendPotw} disabled={sendingPotw} className="bg-[#F5C518]/10 border border-[#F5C518]/50 hover:border-[#F5C518] hover:bg-[#F5C518]/20 text-[#F5C518] text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-60" title="Send Player of the Week"><Trophy size={14} /> <span className="hidden md:inline">{sendingPotw ? "Sending…" : "Send POTW"}</span></button>
          <button data-testid="coach-preview-ebook-btn" onClick={() => navigate("/read/philosophy")} className="hidden sm:flex bg-white/5 border border-[#C41E24]/60 hover:border-[#C41E24] hover:bg-[#C41E24]/10 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors flex items-center gap-2" title="Preview Blueprint"><Eye size={14} className="text-[#C41E24]" /> <span className="hidden md:inline">Preview Blueprint</span></button>
          <button onClick={() => navigate("/")} className="hidden sm:flex text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors items-center gap-2" title="Home"><ArrowLeft size={14} /> <span className="hidden md:inline">Home</span></button>
          <button data-testid="coach-save-btn" onClick={save} disabled={saving} className="bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-60" title="Save Changes"><Save size={14} /> <span className="hidden sm:inline">{saving ? "Saving…" : "Save"}</span></button>
          <button data-testid="coach-logout-btn" onClick={logout} className="text-white/50 hover:text-white p-2" aria-label="Log out"><LogOut size={16} /></button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-10">
        <BusinessPulseBar token={token} onJump={pulseJump} refreshKey={pulseKey} />
        <ScrollFade hintKey="coachTabsHintSeen" testId="coach-tabs-scroll" className="-mx-4 px-4 sm:mx-0 sm:px-0 mb-8 mt-6 sm:mt-8 sm:overflow-visible">
        <div data-testid="coach-tabs" className="coach-tabs flex gap-2 sm:flex-wrap">
          <button data-testid="coach-tab-onfield" onClick={() => setTab("onfield")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "onfield" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}><Radio size={13} /> On-Field</button>
          <button data-testid="coach-tab-content" onClick={() => setTab("content")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "content" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>Content</button>
          <button data-testid="coach-tab-players" onClick={() => setTab("players")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "players" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>Players</button>
          <button data-testid="coach-tab-attendance" onClick={() => setTab("attendance")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "attendance" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>Attendance</button>
          <button data-testid="coach-tab-clubs" onClick={() => setTab("clubs")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "clubs" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>Club Manager</button>
          <button data-testid="coach-tab-trials" onClick={() => setTab("trials")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "trials" ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}>Trial Passes</button>
          <button data-testid="coach-tab-schools" onClick={() => setTab("schools")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "schools" ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}><GraduationCap size={13} /> Institutions</button>
          <button data-testid="coach-tab-readiness" onClick={() => setTab("readiness")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "readiness" ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}><Activity size={13} /> Readiness</button>
          <button data-testid="coach-tab-training" onClick={() => setTab("training")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "training" ? "bg-[#D4AF37] text-black" : "bg-white/5 text-white/60 hover:text-white"}`}><Dumbbell size={13} /> Training</button>
          <button data-testid="coach-tab-highlights" onClick={() => setTab("highlights")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "highlights" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}><Clapperboard size={13} /> Session Highlights</button>
          <button data-testid="coach-tab-recaps" onClick={() => setTab("recaps")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "recaps" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}><Film size={13} /> Recaps</button>
          <button data-testid="coach-tab-freedrills" onClick={() => setTab("freedrills")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 min-h-[44px] rounded-sm transition-colors ${tab === "freedrills" ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>Free Drills</button>
        </div>
        </ScrollFade>
        {tab === "onfield" ? (
          <OnField token={token} />
        ) : tab === "highlights" ? (
          <SessionHighlights token={token} />
        ) : tab === "recaps" ? (
          <CoachRecaps token={token} />
        ) : tab === "freedrills" ? (
          <FreeDrillsManager token={token} />
        ) : tab === "attendance" ? (
          <AttendanceGrid token={token} />
        ) : tab === "players" ? (
          <div className="space-y-6">
            <ClaimLinkTool token={token} />
            <CoachPlayers token={token} openPlayerId={openPlayerId} onPlayerOpened={() => setOpenPlayerId("")}
              initialFilter={playersFilter} onFilterConsumed={() => setPlayersFilter("all")}
              initialSort={playersSort} onSortConsumed={() => setPlayersSort("")}
              returnTo={returnTo}
              onReturn={() => { const r = returnTo; setReturnTo(""); if (r) navigate(r); }} />
          </div>
        ) : tab === "clubs" ? (
          <ClubManager token={token} />
        ) : tab === "trials" ? (
          <TrialPassManager token={token} initialFilter={trialsFilter} jumpKey={pulseKey} />
        ) : tab === "schools" ? (
          <SchoolManager token={token} />
        ) : tab === "readiness" ? (
          <ReadinessAnalytics token={token} />
        ) : tab === "training" ? (
          <div>
            <div className="flex items-center gap-2 mb-2"><Dumbbell size={18} className="text-[#D4AF37]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Training Plans</h2></div>
            <p className="text-sm text-white/45 mb-6 max-w-2xl">The Next O periodized curriculum — In-Season maintenance &amp; injury-prevention and the Off-Season 4-day elite development split. Toggle Boys/Girls for correct season timing.</p>
            <TrainingPlans accent="#C41E24" secondary="#D4AF37" />
          </div>
        ) : (
        <div>
        <CoachPotwHistory />
        <div className="mb-8" data-testid="coach-analytics">
          <div className="flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-[#C41E24]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Player Engagement</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {[
              { label: "App Installs", value: stats?.installs ?? 0, icon: Download },
              { label: "Chapters Completed", value: stats?.total_chapters_completed ?? 0, icon: CheckCircle2 },
              { label: "Move Views", value: stats?.total_move_views ?? 0, icon: Eye },
            ].map((s) => (
              <div key={s.label} className="bg-[#141414] border border-white/10 rounded-lg p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">{s.label}</span>
                  <s.icon size={16} className="text-[#C41E24]" />
                </div>
                <div className="font-cover text-5xl text-white leading-none" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>{s.value}</div>
              </div>
            ))}
          </div>
          <div className="bg-[#141414] border border-white/10 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4"><Star size={15} className="text-[#C41E24]" /><h3 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">Most-Watched Moves</h3></div>
            {stats?.top_moves?.length ? (
              <ol className="space-y-2" data-testid="top-moves-list">
                {stats.top_moves.map((m, i) => (
                  <li key={m.move_id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-sm bg-[#C41E24]/15 text-[#C41E24] font-bold text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="flex-1 text-sm text-white/85 font-medium">{MOVE_NAME[m.move_id] || m.move_id}</span>
                    <span className="text-sm font-bold text-white/60">{m.views} <span className="text-[10px] uppercase tracking-wider text-white/40">views</span></span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-white/40">No move views yet — data appears as players open moves.</p>
            )}
          </div>
        </div>

        <LockerRoomManager token={token} />
        <MarketingKit />

        <div className="bg-[#141414] border border-white/10 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={16} className="text-[#C41E24]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Booking Link</h2></div>
          <p className="text-sm text-white/50 mb-3">Shown on the "Book a Session" buttons throughout the Blueprint.</p>
          <input data-testid="coach-booking-input" type="url" value={booking} onChange={(e) => setBooking(e.target.value)}
            className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
            placeholder="https://nextosoccer.setmore.com/" />
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-2 mb-3"><Star size={16} className="text-[#C41E24]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Featured Video Of The Week</h2></div>
          <p className="text-sm text-white/50 mb-4">Highlighted on the Blueprint cover. Paste a YouTube link to embed it. Leave the video blank to hide the whole section.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Video URL</label>
              <input data-testid="coach-featured-video-input" type="url" value={featVideo} onChange={(e) => setFeatVideo(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                placeholder="https://youtube.com/watch?v=…" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Title</label>
              <input data-testid="coach-featured-title-input" type="text" value={featTitle} onChange={(e) => setFeatTitle(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                placeholder="e.g. Master the Elastico" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Note / Description</label>
              <textarea data-testid="coach-featured-note-input" rows={3} value={featNote} onChange={(e) => setFeatNote(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                placeholder="A short message telling players why to watch this week's video." />
            </div>
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-lg p-6 mb-8" data-testid="coach-weekly-card">
          <div className="flex items-center gap-2 mb-3"><CalendarRange size={16} className="text-[#C41E24]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Weekly Training Session</h2></div>
          <p className="text-sm text-white/50 mb-4">Pick <span className="text-white/70">3 drills</span> for this week's session (players earn <span className="text-white/70">+5 bonus points</span> for finishing all three). Add an optional YouTube "Coach's Tip" — leave the video blank to hide the player. Rotate the drills each week to give players a fresh +5 to chase.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Drill {i + 1}</label>
                <select data-testid={`coach-weekly-drill-${i}`} value={weeklyDrills[i] || ""}
                  onChange={(e) => setWeeklyDrills((prev) => { const n = [...prev]; n[i] = e.target.value; return n; })}
                  className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]">
                  <option value="">— Select a drill —</option>
                  {MOVES.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Coach's Tip — YouTube URL</label>
              <input data-testid="coach-weekly-video-input" type="url" value={weeklyTipVideo} onChange={(e) => setWeeklyTipVideo(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                placeholder="https://youtube.com/watch?v=… (blank = hidden)" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1.5 block">Coach's Tip — Title</label>
              <input data-testid="coach-weekly-title-input" type="text" value={weeklyTipTitle} onChange={(e) => setWeeklyTipTitle(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                placeholder="e.g. Sharpen Your First Touch" />
            </div>
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-lg p-6 mb-8" data-testid="coach-help-videos-card">
          <div className="flex items-center gap-2 mb-3"><LifeBuoy size={16} className="text-[#C41E24]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Help Center — Walkthrough Videos</h2></div>
          <p className="text-sm text-white/50 mb-4">Add links to your walkthrough videos. They appear in the player's <span className="text-white/70">Help</span> panel (the life-ring button in the Hub header), above the FAQ.</p>
          <div className="space-y-3">
            {helpVids.map((v, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`coach-help-video-row-${i}`}>
                <input
                  data-testid={`coach-help-video-title-${i}`}
                  type="text" value={v.title || ""}
                  onChange={(e) => setHelpVids((prev) => prev.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                  placeholder="Title (e.g. Getting Started)"
                  className="sm:col-span-4 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
                <input
                  data-testid={`coach-help-video-url-${i}`}
                  type="url" value={v.url || ""}
                  onChange={(e) => setHelpVids((prev) => prev.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                  placeholder="https://youtube.com/watch?v=…"
                  className="sm:col-span-7 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
                <button data-testid={`coach-help-video-remove-${i}`} onClick={() => setHelpVids((prev) => prev.filter((_, j) => j !== i))}
                  className="sm:col-span-1 flex items-center justify-center text-white/40 hover:text-[#C41E24] transition-colors py-2" aria-label="Remove video"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <button data-testid="coach-help-video-add" onClick={() => setHelpVids((prev) => [...prev, { title: "", url: "" }])}
            className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C41E24] hover:text-white border border-[#C41E24]/50 hover:border-[#C41E24] px-3 py-2 rounded-sm transition-colors">
            <Plus size={14} /> Add Walkthrough Video
          </button>
        </div>

        <div className="bg-[#141414] border border-white/10 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2"><Video size={16} className="text-[#C41E24]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Chapter Videos</h2></div>
            <button data-testid="toggle-chapter-videos" onClick={() => setShowChapterVids((v) => !v)}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors flex-shrink-0">
              <ChevronDown size={15} className={`transition-transform ${showChapterVids ? "rotate-180" : ""}`} /> {showChapterVids ? "Hide" : "Show All (6)"}
            </button>
          </div>
          <p className="text-sm text-white/50 mb-4">Add two YouTube links per chapter — <span className="text-white/70">Watch the Coach</span> and <span className="text-white/70">Watch a Player</span>. The moves chapters show the moves in action; the training chapters show example workouts. Each video only appears in the app when its link is set — leave a field blank to hide just that video.</p>
          {showChapterVids && (<div className="space-y-4">
            {[
              { id: "technical-touches", label: "Ch 4 · Technical Touches" },
              { id: "technical-moves", label: "Ch 5 · Technical Moves" },
              { id: "combination-moves", label: "Ch 6 · Combination & Flow" },
              { id: "balance", label: "Ch 6 · Balance & Coordination (workout)" },
              { id: "vision", label: "Ch 7 · Vision & Scanning (workout)" },
              { id: "agility", label: "Ch 8 · Agility & Speed (workout)" },
            ].map((c) => (
              <div key={c.id} className="border border-white/10 rounded-md p-4">
                <label className="text-sm font-semibold text-white/85 block mb-3">{c.label}</label>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                    <span className="text-xs font-bold uppercase tracking-widest text-white/55">Watch the Coach</span>
                    <input
                      data-testid={`coach-chapter-video-${c.id}-coach`}
                      type="url" value={chapterVids[`${c.id}:coach`] || ""}
                      onChange={(e) => setChapterVids((prev) => ({ ...prev, [`${c.id}:coach`]: e.target.value }))}
                      className="sm:col-span-3 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                      placeholder="https://youtube.com/watch?v=…"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                    <span className="text-xs font-bold uppercase tracking-widest text-white/55">Watch a Player</span>
                    <input
                      data-testid={`coach-chapter-video-${c.id}-player`}
                      type="url" value={chapterVids[`${c.id}:player`] || ""}
                      onChange={(e) => setChapterVids((prev) => ({ ...prev, [`${c.id}:player`]: e.target.value }))}
                      className="sm:col-span-3 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                      placeholder="https://youtube.com/watch?v=…"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>)}
        </div>

        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2"><Video size={18} className="text-[#C41E24]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Move Demo Videos</h2></div>
          <button data-testid="toggle-move-videos" onClick={() => setShowMoveVids((v) => !v)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors flex-shrink-0">
            <ChevronDown size={15} className={`transition-transform ${showMoveVids ? "rotate-180" : ""}`} /> {showMoveVids ? "Hide" : `Show All (${MOVES.length})`}
          </button>
        </div>
        <p className="text-sm text-white/50 mb-6">Paste a YouTube link to embed it directly in that move's dialog. Leave blank to fall back to an auto YouTube search. Any video link also works as a button.</p>

        {showMoveVids && (<>
        {["touch", "move", "combo"].map((tier) => (
          <div key={tier} className="mb-8">
            <h3 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/60 mb-3 border-b border-white/10 pb-2">{TIER_TITLES[tier]}</h3>
            <div className="space-y-3">
              {grouped[tier].map((m) => (
                <div key={m.id} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
                  <label className="text-sm font-semibold text-white/85 sm:col-span-1">{m.name}</label>
                  <input
                    data-testid={`coach-video-input-${m.id}`}
                    type="url"
                    value={overrides[m.id] || ""}
                    onChange={(e) => setOverrides((prev) => ({ ...prev, [m.id]: e.target.value }))}
                    className="sm:col-span-2 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                    placeholder="https://youtube.com/watch?v=…"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        </>)}

        <button data-testid="coach-save-btn-bottom" onClick={save} disabled={saving} className="bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-60"><Save size={16} /> {saving ? "Saving…" : "Save Changes"}</button>
        </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
}
